import React, { useContext, useEffect, useState } from 'react';

import { ArrowAngleRight, OrderDetail, Paw, Shop } from '@zooplus/icons';
import { ButtonAnchor } from '@zooplus/ui-components';

import LoadingSkeleton from '../../common/components/LoadingSkeleton';
import LocResourcesContext from '../../common/contexts/LocResourcesContext';
import PublicMenuContext from '../contexts/PublicMenuContext';

type PublicMenuComponentProps = {
  selectedMenuItem: string;
  bpEnabled: boolean;
};

const PublicMenuComponent: React.FC<PublicMenuComponentProps> = ({
  selectedMenuItem,
  bpEnabled,
}) => {
  const GLOBAL_PREFIX: string = 'loy-bppo-rb';
  const SET_TIMEOUT_TIMER = 10000;
  const { onFormatLocale, locResources } = useContext(LocResourcesContext);
  const { onSendingMetricEvent } = useContext(PublicMenuContext);
  const [isPublicMenuDataReady, setIsPublicMenuDataReady] = useState<boolean>(false);
  const [isRenderFailed, setIsRenderFailed] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const renderSuccess = () => {
    setIsRenderFailed(false);
    onSendingMetricEvent('renderSuccess');
  };

  useEffect(() => {
    if (isPublicMenuDataReady) {
      renderSuccess();
    }
    const metricsTimeout = setTimeout(() => {
      if (!isPublicMenuDataReady) setIsRenderFailed(true);
    }, SET_TIMEOUT_TIMER);
    return function () {
      clearTimeout(metricsTimeout);
    };
  }, [isPublicMenuDataReady]);

  useEffect(() => {
    setIsPublicMenuDataReady(locResources?.length !== 0);
  }, [locResources]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const publicMenuComponent = () => {
    return (
      <nav
        className={`${GLOBAL_PREFIX}-publicMenuNavigation`}
        aria-label={onFormatLocale?.('bp.publicMenu.navigation')}>
        <button
          className={`${GLOBAL_PREFIX}-toggleButton ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}>
          {onFormatLocale?.('mzp.navigation.mobileMenuTitle')}
          <ArrowAngleRight
            className={`${GLOBAL_PREFIX}-angleIcon ${isMobileMenuOpen ? 'open' : ''}`}
            aria-hidden
          />
        </button>
        <ul className={`${GLOBAL_PREFIX}-menu ${!isMobileMenuOpen ? 'closed' : ''}`}>
          {bpEnabled && (
            <li
              className={`${GLOBAL_PREFIX}-menuItem ${selectedMenuItem === 'bonuspoints.nav.overview' ? 'selected' : ''}`}>
              <Paw className={`${GLOBAL_PREFIX}-itemIcon`} />
              <a
                href="/bonuspoints/overview"
                className={`${GLOBAL_PREFIX}-menuLink`}
                aria-label={onFormatLocale?.('bp.publicMenu.link.howItWorks')}>
                <span>{onFormatLocale?.('bp.howitworks')}</span>
                <ArrowAngleRight className={`${GLOBAL_PREFIX}-linkAngleIcon`} />
              </a>
            </li>
          )}
          <li
            className={`${GLOBAL_PREFIX}-menuItem ${selectedMenuItem === 'bonuspoints.nav.products' ? 'selected' : ''}`}>
            <Shop className={`${GLOBAL_PREFIX}-itemIcon`} />
            <a
              href="/bonuspoints/products"
              className={`${GLOBAL_PREFIX}-menuLink`}
              aria-label={onFormatLocale?.('bp.publicMenu.link.products')}>
              <span>{onFormatLocale?.('bp.products')}</span>
              <ArrowAngleRight className={`${GLOBAL_PREFIX}-linkAngleIcon`} />
            </a>
          </li>
          <li
            className={`${GLOBAL_PREFIX}-menuItem ${selectedMenuItem === 'bonuspoints.nav.terms' ? 'selected' : ''}`}>
            <OrderDetail className={`${GLOBAL_PREFIX}-itemIcon`} />
            <a
              href="/legal/bonus_points_terms"
              className={`${GLOBAL_PREFIX}-menuLink`}
              aria-label={onFormatLocale?.('bp.publicMenu.link.terms')}>
              <span>{onFormatLocale?.('bp.terms')}</span>
              <ArrowAngleRight className={`${GLOBAL_PREFIX}-linkAngleIcon`} />
            </a>
          </li>
          <li className={`${GLOBAL_PREFIX}-menuButton`}>
            <ButtonAnchor href={'/account/bonuspoints/products/overview'} length="full">
              {onFormatLocale?.('general.button.signin')}
            </ButtonAnchor>
          </li>
        </ul>
      </nav>
    );
  };

  return isPublicMenuDataReady ? (
    <>{publicMenuComponent()}</>
  ) : isRenderFailed ? (
    <div></div>
  ) : (
    <LoadingSkeleton marginTop={15} height={'230px'}></LoadingSkeleton>
  );
};

export default PublicMenuComponent;
